import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { postsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { DialogConfirm } from 'components/_new/Dialog';
import { Link } from 'components/_new/Link';
import { Spinner } from 'components/_new/Spinner';
import { useAuth } from 'contexts/authContext';
import { useProgramPanel } from 'contexts/programPanelContext';
import { EmptyState } from 'utils/emptyState';
import { Post } from 'types/Dashboard';
import { Paginated } from 'types/Paginated';
import NotificationPost from '../wall/NotificationPost';
import { Button } from '../_new/Button';

export const ProgramPostList = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: program, panelPath } = useProgramPanel();
	const postsPerLoad = 8;

	const queryClient = useQueryClient();

	const { data, fetchNextPage, isLoading, isFetchingNextPage, isSuccess, hasNextPage } = useInfiniteQuery(
		[
			'getProgramAdminPosts',
			{
				programId: Number(program?.id),
			},
		],
		({ pageParam }) =>
			postsApi.getPostsAdmin({
				programId: Number(program?.id),
				params: { page: pageParam || 1, pageSize: postsPerLoad },
			}),
		{
			onSuccess: ({ pages }) => pages,
			enabled: true,
			keepPreviousData: true,
			getNextPageParam: ({ pagination }) => {
				const nextPage = pagination.currentPage + 1;
				if (nextPage > pagination.itemsPerPage) {
					return false;
				}
				return nextPage;
			},
		}
	);

	const [deletedPosts, setDeletedPosts] = useState<number[]>([]);
	const posts = useMemo(
		() =>
			(data?.pages || [])
				.reduce<Post[]>((current, item) => {
					return [...current, ...item.data];
				}, [])
				.filter((post) => !deletedPosts.includes(post.id)),
		[data, deletedPosts]
	);

	const { mutate: deletePost } = useMutation(['deleteProgramAdminPost'], postsApi.deletePostAdmin, {
		onSuccess: (data, variables) => {
			setDeletedPosts((prev) => [...prev, variables.postId]);
			setPostToDelete(null);
			queryClient.setQueriesData<InfiniteData<Paginated<Post>>>(
				[
					'getProgramAdminPosts',
					{
						programId: Number(program?.id),
					},
				],
				(data) => {
					if (data) {
						return {
							...data,
							pages: data?.pages.map((page) => ({
								...page,
								data: page.data.filter((post) => post.id !== variables.postId),
							})),
						};
					}
					return data;
				}
			);
		},
	});

	const [postToDelete, setPostToDelete] = useState<any | null>(null);
	const [deletePostDialogOpen, setDeletePostDialogOpen] = useState(false);

	const handleConfirmDeletePost = () => {
		if (program && postToDelete) {
			deletePost({
				postId: postToDelete.id,
			});
		}
	};

	return (
		<>
			<Helmet title={t('organizationPanel.programCommunication')} />
			<SubPageTitle
				title={t('organizationPanel.programCommunication')}
				additional={program?.displayName}
				right={
					<div className="flex flex-row flex-wrap gap-2 md:justify-content-end">
						<Button
							className=" p-button p-button-sm min-w-max max-h-3rem"
							label={t('programPanel.post.showAutomaticNotifications')}
							onClick={() => navigate(`/program/${program?.name}/communication/mentibot`)}
							variant="primary-outlined"
						/>
						<Button
							className=" p-button p-button-sm min-w-max max-h-3rem"
							label={t('programPanel.post.addNew')}
							onClick={() => navigate(`/program/${program?.name}/communication/add`)}
						/>
					</div>
				}
			/>

			<div className="text-sm px-1 mb-3 mx-1">
				<p>{t('programPanel.post.info1')}</p>
				<p>
					<Trans
						t={t}
						i18nKey="programPanel.post.info2"
						components={[<Link to={`/${panelPath}/communication/mentibot`} />]}
					/>
				</p>
			</div>

			{isLoading && <Spinner />}
			{!isLoading && isSuccess && posts && posts.length === 0 && (
				<EmptyState title={t('programPanel.post.noPostsAction')} />
			)}
			{isSuccess && posts && posts.length > 0 && (
				<div className="flex flex-column gap-4">
					{posts.map((post) => (
						<div key={post.id} className="flex flex-column gap-2">
							<NotificationPost
								key={post.id}
								postData={post}
								withComments
								adminMode
								getPostCommentsRequest={({ postId, pageParam }) => {
									return postsApi.getPostCommentsAdmin({
										postId,
										params: {
											page: pageParam[0] || 1,
											limit: 3,
										},
									});
								}}
								addPostCommentRequest={({ postId, payload }) => {
									return postsApi.addPostCommentAdmin({ postId, payload });
								}}
							/>
							<div className="flex flex-row justify-content-end gap-2">
								<Button
									icon="pencil"
									onClick={() => {
										navigate(`/program/${program?.name}/communication/edit/${post.id}`);
									}}
									label={t('actions.edit')}
									className=" p-button p-button-sm min-w-max max-h-3rem"
									variantSize="sm"
								/>
								<Button
									icon="trash"
									onClick={() => {
										setPostToDelete(post);
										setDeletePostDialogOpen(true);
									}}
									label={t('actions.remove')}
									className="min-w-max max-h-3rem"
									variant="danger"
									variantSize="sm"
								/>
							</div>
						</div>
					))}
				</div>
			)}
			{hasNextPage && posts && posts.length > 0 && (
				<div className="flex justify-content-center">
					<Button label={t('misc.loadMore')} onClick={() => fetchNextPage()} loading={isFetchingNextPage} />
				</div>
			)}
			<DialogConfirm
				visible={deletePostDialogOpen}
				onHide={() => setDeletePostDialogOpen(false)}
				message={t('programPanel.post.confirmRemove')}
				onConfirm={handleConfirmDeletePost}
			/>
		</>
	);
};
